import { OrderedList } from '@tiptap/extension-ordered-list';
import { Plugin } from 'prosemirror-state';
import { PluginKey } from '@tiptap/pm/state';
import {
  normalizeOrderedMarkerColor,
  postEditorOrderedListMarkerClass,
  parseOrderedMarkerFromClassList,
} from '../lib/post_editor_constants.js';
import { selectionInHeadingOrSubheadingParagraph } from '../lib/post_editor_paragraph_style.js';
import {
  listAttrsForIndent,
  normalizeIndentedListItemsInRange,
} from '../lib/post_list_indent.js';
import { stripInlineMarksInOrderedLists } from '../lib/post_editor_inline_marks.js';

const stripOlInlineColorsKey = new PluginKey('stripOrderedListInlineColors');

export const PathstoneOrderedList = OrderedList.extend({
  addCommands() {
    return {
      toggleOrderedList:
        () =>
        ({ chain, state }) => {
          if (selectionInHeadingOrSubheadingParagraph(state)) return false;

          const listAttrs = listAttrsForIndent(state);
          const normalizeLists = () =>
            ({ tr }) => {
              const { from, to } = tr.selection;
              normalizeIndentedListItemsInRange(tr, from, to);
              return true;
            };

          /** Body inline text colors are not valid in OL; strip color after wrap (font families stay). */
          const stripBodyInlineColorsInOl = () =>
            ({ tr, state }) => {
              stripInlineMarksInOrderedLists(tr, tr.doc, state.schema);
              return true;
            };

          if (this.options.keepAttributes) {
            return chain()
              .toggleList(this.name, this.options.itemTypeName, this.options.keepMarks, listAttrs)
              .updateAttributes(this.options.itemTypeName, this.editor.getAttributes('textStyle'))
              .command(stripBodyInlineColorsInOl)
              .command(normalizeLists)
              .run();
          }

          return chain()
            .toggleList(this.name, this.options.itemTypeName, this.options.keepMarks, listAttrs)
            .command(stripBodyInlineColorsInOl)
            .command(normalizeLists)
            .run();
        },
    };
  },

  addProseMirrorPlugins() {
    const parent = this.parent?.() ?? [];
    return [
      ...parent,
      new Plugin({
        key: stripOlInlineColorsKey,
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((t) => t.docChanged)) return null;
          const tr = newState.tr;
          const changed = stripInlineMarksInOrderedLists(tr, newState.doc, newState.schema);
          return changed ? tr : null;
        },
      }),
    ];
  },

  addAttributes() {
    const parentFn = this.parent;
    const parentAttrs = typeof parentFn === 'function' ? parentFn() : {};
    return {
      ...parentAttrs,
      markerColor: {
        default: '#000000',
        parseHTML: (element) => {
          const fromData = element.getAttribute('data-ps-ol-marker');
          if (fromData) return normalizeOrderedMarkerColor(fromData);
          const fromClass = parseOrderedMarkerFromClassList(element.classList);
          if (fromClass) return normalizeOrderedMarkerColor(fromClass);
          const style = element.getAttribute('style') || '';
          const m = style.match(/--ps-ol-marker:\s*([^;]+)/i);
          if (m) return normalizeOrderedMarkerColor(m[1].trim());
          return '#000000';
        },
        renderHTML: (attributes) => {
          const color = normalizeOrderedMarkerColor(attributes.markerColor);
          return {
            'data-ps-ol-marker': color,
            class: `ps-post-ordered-list ${postEditorOrderedListMarkerClass(color)}`,
          };
        },
      },
    };
  },
});
